// Модели данных для графиков ММБ и СМБ.
// Формат ответа описан в DOC/Инструкция для фронта - графики ММБ и СМБ.md

export type ChartResolution = "day" | "week" | "month";

export type DeviceChartType = "mdbn" | "mmb" | "smb";

export type RetroChartId = "line" | "module" | "sub_devices" | "stations";

/** Реальная точка внутри online-сегмента. Unix-секунды (для ECharts умножаем на 1000). */
export interface ChartPoint {
  timestamp: number;
  /** 0 — реальный ноль или слот без данных при живом устройстве */
  value: number;
  /** Опционально: реальное время, когда в слоте достигнут максимум */
  period?: { actualTime?: number };
}

/** Живой участок серии: реальные точки. */
export interface OnlineSegment {
  type: "online";
  data: ChartPoint[];
}

/** Разрыв линии (обрыв связи / будущее): точки не передаются. */
export interface OfflineSegment {
  type: "offline";
  from: number;
  to: number;
}

export type Segment = OnlineSegment | OfflineSegment;

export interface ChartSeries {
  name: string;
  /** Список чередующихся online/offline сегментов по возрастанию времени. */
  segments: Segment[];
}

export interface RetroChart {
  id: RetroChartId;
  title: string;
  series: ChartSeries[];
}

export interface LimitPoint {
  /** Unix-секунды */
  timestamp: number;
  lineLimit?: number;
  moduleLimit?: number;
}

export interface DeviceChartData {
  deviceType: DeviceChartType;
  limits: LimitPoint[];
  charts: RetroChart[];
}

/** Пустой график = все серии не содержат ни одного online-сегмента с точками */
export function isRetroChartEmpty(chart: RetroChart): boolean {
  return chart.series.every((series) =>
    series.segments.every(
      (segment) =>
        segment.type !== "online" || segment.data.length === 0,
    ),
  );
}

export type EChartsPoint = [number, number | null];

/**
 * Разворачивает сегменты серии в плоский ряд точек для ECharts:
 * - online-сегменты → точки [timestamp*1000, value];
 * - перед каждым offline-сегментом вставляем точку-разрыв [from*1000, null],
 *   чтобы линия обрывалась на интервале обрыва (при connectNulls: false).
 * Возвращает массив точек и true, если серия содержит хотя бы одну реальную точку.
 */
export function flattenSegments(segments: Segment[]): {
  points: EChartsPoint[];
  hasData: boolean;
} {
  const points: EChartsPoint[] = [];
  let hasData = false;

  segments.forEach((segment) => {
    if (segment.type === "online") {
      segment.data.forEach((point) => {
        points.push([point.timestamp * 1000, point.value]);
        hasData = true;
      });
    } else {
      // Разрыв линии в начале интервала обрыва
      points.push([segment.from * 1000, null]);
    }
  });

  return { points, hasData };
}
